// Seite „Exporte" (/exports): alle eigenen Kanal-Exporte. Vertrag: docs/API.md 3.12.
import type { ExportJobDto } from '@lagerfeuer/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { exportsApi } from '../api';
import { formatDateTime } from '../format';
import { channelLabel, useChannelLookup } from '../scheduled/channels';
import { ConfirmButton, Empty, ErrorBox, ExtrasPage, Loading } from '../ui';
import { ExportStatus } from './ExportStatus';
import { EXPORT_POLL_MS, EXPORTS_KEY, EXPORTS_LIST_KEY, FORMAT_LABEL, isExportActive, sortExports } from './realtime';
import './exports.css';

export function ExportsPage() {
  const list = useQuery({
    queryKey: EXPORTS_LIST_KEY,
    queryFn: exportsApi.list,
    select: sortExports,
    refetchInterval: (q) => (q.state.data?.some((e) => isExportActive(e.status)) ? EXPORT_POLL_MS : false),
  });
  const items = list.data ?? [];
  const channels = useChannelLookup(items.map((e) => e.channelId));
  return (
    <ExtrasPage title="Exporte" testId="exports-page">
      <p className="lfx-muted lfx-exp-p">Exporte startest du über die Kanal-Kopfzeile („Kanal exportieren"). Fertige Dateien werden nach Ablauf automatisch gelöscht.</p>
      {list.isPending ? (
        <Loading testId="exports-loading" />
      ) : list.isError ? (
        <ErrorBox error={list.error} testId="exports-error" />
      ) : items.length === 0 ? (
        <Empty testId="exports-empty">Noch keine Exporte.</Empty>
      ) : (
        <ul className="lfx-exp-list" data-testid="exports-list">
          {items.map((job) => (
            <ExportItem key={job.id} job={job} channelName={channelLabel(channels.get(job.channelId))} />
          ))}
        </ul>
      )}
    </ExtrasPage>
  );
}

function ExportItem({ job, channelName }: { job: ExportJobDto; channelName: string }) {
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: () => exportsApi.remove(job.id),
    onSuccess: () => {
      qc.setQueryData<ExportJobDto[]>(EXPORTS_LIST_KEY, (l) => l?.filter((e) => e.id !== job.id));
      void qc.invalidateQueries({ queryKey: EXPORTS_KEY });
    },
  });
  return (
    <li className="lfx-card lfx-exp-item" data-testid="exports-item" data-id={job.id} data-status={job.status}>
      <div className="lfx-row lfx-row--between">
        <div className="lfx-row">
          <strong data-testid="exports-item-channel">{channelName}</strong>
          <span className="lfx-badge" data-testid="exports-item-format">
            {FORMAT_LABEL[job.format]}
          </span>
        </div>
        <span className="lfx-muted">gestartet {formatDateTime(job.createdAt)}</span>
      </div>
      <ExportStatus job={job} testId="exports-item" />
      <ErrorBox error={remove.error} testId="exports-item-error" />
      <div className="lfx-row">
        <ConfirmButton label="Löschen" confirmLabel="Wirklich löschen?" testId="exports-item-delete" disabled={remove.isPending} onConfirm={() => remove.mutate()} />
      </div>
    </li>
  );
}
